import { useAppSelector } from "../state/store";
import {
  AIRouterSettings,
  AIRouterStatus,
  DEFAULT_AI_ROUTER_SETTINGS,
  isAiRouterEnabled as checkAiRouterEnabled,
  isAiRouterExplicitlyEnabled as checkAiRouterExplicitlyEnabled,
} from "../models/aiRouterSettings";

/**
 * Custom hook to access AI Router Settings from Redux state
 * Replaces the old isVersionApplicabletoPlan("ai-triggers") check
 * 
 * The settings are fetched and stored in Redux by chatbot-builder/index.tsx
 * when the app initializes.
 * 
 * Both "enabled" flags stay false until the settings load, so a store that saved
 * DISABLED never sees the AI toggle flash on.
 *
 * @returns Object containing:
 *   - aiRouterSettings: The current AI Router settings
 *   - isAiRouterSettingsLoaded: Whether the settings have been fetched
 *   - isAiRouterEnabled: Whether AI Router is on for WhatsApp / Instagram (anything but DISABLED)
 *   - isAiRouterExplicitlyEnabled: Whether the store saved ENABLED or TEST_MODE (used by Email)
 *   - isAiRouterDisabled: Whether AI Router is disabled
 *   - isAiRouterInTestMode: Whether AI Router is in test mode only
 */
import { useDispatch } from "react-redux";
import { homeDataAction } from "../state/homeState";
import { FirebaseService } from "../services/firebase/FirebaseService";

export const useAiRouterSettings = () => {
  const homeState = useAppSelector((state) => state.homeState);
  const dispatch = useDispatch();
  const aiRouterSettings: AIRouterSettings = homeState?.aiRouterSettings ?? DEFAULT_AI_ROUTER_SETTINGS;
  const isAiRouterSettingsLoaded = homeState?.isAiRouterSettingsLoaded ?? false;

  const fetchAndSetAiRouterSettings = (storeId: string, firebaseService: FirebaseService) => {
    // Fetch AI Router settings from chatflows/<store_id> document
    firebaseService
      .getAiRouterSettings(storeId || "")
      .then((settings) => {
        dispatch(
          homeDataAction.updateState({
            aiRouterSettings: settings,
            isAiRouterSettingsLoaded: true,
          })
        );
      })
      .catch((err) => {
        console.error("[useAiRouterSettings] Error fetching AI Router settings:", err);
      });
  };

  return {
    aiRouterSettings,
    isAiRouterSettingsLoaded,
    isAiRouterEnabled: isAiRouterSettingsLoaded && checkAiRouterEnabled(aiRouterSettings),
    isAiRouterExplicitlyEnabled:
      isAiRouterSettingsLoaded && checkAiRouterExplicitlyEnabled(aiRouterSettings),
    isAiRouterDisabled: aiRouterSettings.status === AIRouterStatus.DISABLED,
    isAiRouterInTestMode: aiRouterSettings.status === AIRouterStatus.TEST_MODE,
    fetchAndSetAiRouterSettings,
  };
};
